import React, { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import type { IFormElement } from '../types';
import { getFieldComponent } from '../utils/fieldMapper';
import { evaluateVisibility } from '../utils/visibility';
import {
  TextField,
  TextAreaField,
  DropdownField,
  RadioGroupField,
  CheckboxField,
  BooleanField,
  NumberField,
  RatingField,
  FileUploadField,
  ImagePickerField,
  RankingField,
  MatrixField,
  MatrixDropdownField,
  MatrixDynamicField,
  MultipleTextField,
  TagboxField,
  HtmlField,
  ExpressionField,
  PanelField,
} from '../fields';

export interface FormFieldProps {
  element: IFormElement;
  questionNumber?: number;
  totalQuestions?: number;
  renderPanel?: (
    element: IFormElement,
    children: React.ReactNode
  ) => React.ReactNode;
}

export const FormField: React.FC<FormFieldProps> = ({
  element,
  questionNumber,
  renderPanel,
}) => {
  const { watch } = useFormContext();
  const formValues = watch();

  const referencedFields = useMemo(() => {
    if (!element.visibleIf) return [];
    const fieldRegex = /\{([a-zA-Z_$][a-zA-Z0-9_$]*)\}/g;
    const matches = Array.from(element.visibleIf.matchAll(fieldRegex));
    return matches.map((match) => match[1]);
  }, [element.visibleIf]);

  const hasReferencedFieldValue = useMemo(() => {
    if (referencedFields.length === 0) return true;

    const hasValue = referencedFields.some((fieldName) => {
      const value = formValues[fieldName];

      if (value === undefined || value === null) return false;
      if (value === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;

      return true;
    });
    // Este console pode ser útil para debugar melhor a lógica de visibilidade dos campos
    // console.log('🔍 hasReferencedFieldValue check:', {
    //   element: element.name,
    //   visibleIf: element.visibleIf,
    //   referencedFields,
    //   formValues: referencedFields.reduce((acc, field) => {
    //     acc[field] = formValues[field];
    //     return acc;
    //   }, {} as Record<string, any>),
    //   hasValue,
    // });

    return hasValue;
  }, [referencedFields, formValues, element.name, element.visibleIf]);

  if (element.visibleIf) {
    if (!hasReferencedFieldValue) {
      return null;
    } else {
      const shouldBeVisible = evaluateVisibility(element.visibleIf, formValues);
      // Este console pode ser útil para debugar melhor a lógica de visibilidade dos campos
      // console.log('🎯 Avaliando regra:', {
      //   element: element.name,
      //   visibleIf: element.visibleIf,
      //   shouldBeVisible
      // });

      if (!shouldBeVisible) {
        return null;
      }
    }
  }

  const fieldType = getFieldComponent(element);

  if (element.type === 'panel' || element.type === 'paneldynamic') {
    const children = (element.elements || []).map((childElement, index) => (
      <FormField
        key={childElement.name || index}
        element={childElement}
        renderPanel={renderPanel}
      />
    ));

    if (renderPanel) {
      return <>{renderPanel(element, children)}</>;
    }

    return <PanelField element={element}>{children}</PanelField>;
  }

  switch (fieldType) {
    case 'text':
      return <TextField element={element} questionNumber={questionNumber} />;

    case 'comment':
    case 'textarea':
      return (
        <TextAreaField element={element} questionNumber={questionNumber} />
      );

    case 'dropdown':
      return (
        <DropdownField element={element} questionNumber={questionNumber} />
      );

    case 'radiogroup':
      return (
        <RadioGroupField element={element} questionNumber={questionNumber} />
      );

    case 'checkbox':
      return (
        <CheckboxField element={element} questionNumber={questionNumber} />
      );

    case 'boolean':
      return <BooleanField element={element} questionNumber={questionNumber} />;

    case 'number':
      return <NumberField element={element} questionNumber={questionNumber} />;

    case 'rating':
      return <RatingField element={element} questionNumber={questionNumber} />;

    case 'file':
      return (
        <FileUploadField element={element} questionNumber={questionNumber} />
      );

    case 'imagepicker':
      return (
        <ImagePickerField element={element} questionNumber={questionNumber} />
      );

    case 'ranking':
      return <RankingField element={element} questionNumber={questionNumber} />;

    case 'matrix':
      return <MatrixField element={element} questionNumber={questionNumber} />;

    case 'matrixdropdown':
      return (
        <MatrixDropdownField
          element={element}
          questionNumber={questionNumber}
        />
      );

    case 'matrixdynamic':
      return (
        <MatrixDynamicField element={element} questionNumber={questionNumber} />
      );

    case 'multipletext':
      return (
        <MultipleTextField element={element} questionNumber={questionNumber} />
      );

    case 'tagbox':
      return <TagboxField element={element} questionNumber={questionNumber} />;

    case 'html':
      return <HtmlField element={element} questionNumber={questionNumber} />;

    case 'expression':
      return (
        <ExpressionField
          element={element}
          formValues={formValues}
          questionNumber={questionNumber}
        />
      );

    default:
      console.warn(`Unknown field type: ${element.type}, falling back to text`);
      return <TextField element={element} questionNumber={questionNumber} />;
  }
};

export default FormField;
